import { type InfiniteData, type QueryKey } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";

import { queryClient } from "@/api/core";
import { getActualStatus } from "@/utils/status";

import type { TrackTarget } from "./types";
import type { Status as StatusEntity } from "@/schemas";

// ── Target ──

/** Derive the payload-relevant fields from a rendered status. */
const statusTrackingTarget = (status: StatusEntity): TrackTarget | null => {
  const actual = getActualStatus(status);
  if (!actual) return null;

  const isVideo = actual.media_attachments?.some(
    (a) => a.type === "video" || a.type === "gifv",
  );

  return {
    itemId: status.id,
    // status_id and signature must come from the SAME served status (the wrapper for a retruth):
    // sevro mints a signature per status id, so an inner id verified against the wrapper's
    // signature is dropped as not_member. Only the author comes from the displayed inner content.
    statusId: status.id,
    authorAccountId: actual.account?.id,
    announcementId: actual.embedded_announcement?.id,
    // Signature is served on the wrapper only; no inner fallback (it can never verify against the
    // wrapper id). null (not served) -> undefined so it's omitted from the payload.
    signature: status.signature ?? undefined,
    isReblog: !!(status.reblog && typeof status.reblog === "object"),
    isQuote: !!actual.quote,
    isVideo: !!isVideo,
  };
};

// ── Position ──

type CachedTimeline = InfiniteData<{ result: { id: string }[] }>;

/**
 * Absolute 0-based position of `itemId` in server order, cumulative across pages, counting only
 * server truths (ad placeholders excluded). null when there's no feed key or the item isn't
 * found — the impression is still sent, just with position omitted.
 */
const resolvePosition = (
  queryKey: QueryKey | null,
  itemId: string,
): number | null => {
  if (!queryKey) return null;

  const data = queryClient.getQueryData<CachedTimeline>(queryKey);
  if (!data?.pages) return null;

  let count = 0;
  for (const page of data.pages) {
    for (const item of page.result ?? []) {
      if (item.id === itemId) return count;
      if (!item.id.endsWith("-ad")) count++; // ads don't get a position, don't shift it
    }
  }
  return null;
};

// ── Ids ──

const newEventId = (): string => uuidv4();

export { statusTrackingTarget, resolvePosition, newEventId };
